import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, StatCard, TableLoadingSkeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, MagnifyingGlass, Prohibit, CheckCircle, WarningCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const inrFull = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);

const MODE_LABEL = { cash: "Cash", cheque: "Cheque", upi: "UPI", bank_transfer: "Bank Transfer", online: "Online", other: "Other" };
const STATUS_VARIANT = { confirmed: "success", cancelled: "danger", partial: "warn", paid: "success", unpaid: "neutral" };

export default function Payments() {
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [flats, setFlats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [recordOpen, setRecordOpen] = useState(false);

    // Filters
    const [filters, setFilters] = useState({ q: "", mode: "", status: "", flat_id: "", date_from: "", date_to: "" });

    async function load() {
        setLoading(true);
        try {
            const [pRes, sRes, fRes] = await Promise.all([
                api.get("/payments", { params: cleanParams(filters) }),
                api.get("/payments/summary"),
                api.get("/flats"),
            ]);
            setPayments(pRes.data);
            setSummary(sRes.data);
            setFlats(fRes.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters]);

    async function cancelPayment(p) {
        const reason = window.prompt(`Cancel payment of ${inrFull(p.amount)} received by ${MODE_LABEL[p.payment_mode]} on ${p.payment_date}?\n\nOptional reason:`);
        if (reason === null) return;
        try {
            await api.post(`/payments/${p.id}/cancel`, { reason: reason || null });
            toast.success("Payment cancelled — invoice balance updated");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    const flatById = useMemo(() => Object.fromEntries(flats.map(f => [f.id, f])), [flats]);

    return (
        <div data-testid="payments-page">
            <PageHeader
                overline="Finance"
                title="Payments"
                description="Record and track society payments across invoices, flats and payment methods."
                actions={
                    <Button className="rounded-full" onClick={() => setRecordOpen(true)} data-testid="btn-record-payment">
                        <Plus size={16} className="mr-1.5" /> Record Payment
                    </Button>
                }
            />

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <StatCard
                    label="Collected this month"
                    value={summary ? inr(summary.collected_month.total) : "—"}
                    hint={summary ? `${summary.collected_month.count} payments` : ""}
                    accent
                />
                <StatCard label="Collected today" value={summary ? inr(summary.collected_today.total) : "—"} hint={summary ? `${summary.collected_today.count} payments` : ""} />
                <StatCard label="Outstanding" value={summary ? inr(summary.total_outstanding) : "—"} hint="Across all unpaid invoices" />
                <StatCard label="Partially paid invoices" value={summary?.partial_paid_invoices ?? "—"} hint="Have some balance remaining" />
            </div>

            {/* By-mode breakdown */}
            {summary && (
                <div className="mb-6 grid grid-cols-2 md:grid-cols-6 gap-2">
                    {["cash", "cheque", "upi", "bank_transfer", "online", "other"].map(m => {
                        const v = summary.by_mode_month?.[m] || { total: 0, count: 0 };
                        return (
                            <div key={m} className="border border-brand-line bg-white rounded-sm p-3" data-testid={`sum-mode-${m}`}>
                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{MODE_LABEL[m]}</p>
                                <p className="text-sm font-medium text-brand-ink mt-1">{inr(v.total)}</p>
                                <p className="text-[10px] text-brand-inkSoft">{v.count} pmts · MTD</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-3 mb-4 flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[180px]">
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Search</Label>
                    <div className="relative mt-1">
                        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="filter-q" value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} placeholder="Reference / cheque / notes" className="pl-7 rounded-sm border-brand-line h-9" />
                    </div>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Mode</Label>
                    <Select value={filters.mode || "any"} onValueChange={(v) => setFilters({ ...filters, mode: v === "any" ? "" : v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-36" data-testid="filter-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any mode</SelectItem>
                            {Object.entries(MODE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Status</Label>
                    <Select value={filters.status || "any"} onValueChange={(v) => setFilters({ ...filters, status: v === "any" ? "" : v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-32" data-testid="filter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any status</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Flat</Label>
                    <Select value={filters.flat_id || "any"} onValueChange={(v) => setFilters({ ...filters, flat_id: v === "any" ? "" : v })}>
                        <SelectTrigger className="rounded-sm border-brand-line h-9 mt-1 w-36" data-testid="filter-flat"><SelectValue placeholder="Any flat" /></SelectTrigger>
                        <SelectContent className="max-h-64">
                            <SelectItem value="any">Any flat</SelectItem>
                            {flats.map(f => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">From</Label>
                    <Input data-testid="filter-from" type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="rounded-sm border-brand-line h-9 mt-1" />
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">To</Label>
                    <Input data-testid="filter-to" type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="rounded-sm border-brand-line h-9 mt-1" />
                </div>
                {(filters.q || filters.mode || filters.status || filters.flat_id || filters.date_from || filters.date_to) && (
                    <Button variant="outline" size="sm" className="rounded-full h-9" onClick={() => setFilters({ q: "", mode: "", status: "", flat_id: "", date_from: "", date_to: "" })}>Clear</Button>
                )}
            </div>

            {/* Table */}
            {loading ? <TableLoadingSkeleton rows={5} /> : payments.length === 0 ? (
                <EmptyState
                    title="No payments to show"
                    description="Record your first payment to see it here. Filters may also be hiding rows."
                    action={<Button className="rounded-full" onClick={() => setRecordOpen(true)}><Plus size={16} className="mr-1.5" /> Record Payment</Button>}
                />
            ) : (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="payments-table">
                        <thead className="bg-brand-bg">
                            <tr className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Flat</th>
                                <th className="px-3 py-2 text-left">Resident</th>
                                <th className="px-3 py-2 text-left">Invoice</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-left">Mode</th>
                                <th className="px-3 py-2 text-left">Reference</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-left">Recorded By</th>
                                <th className="px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map(p => (
                                <tr key={p.id} className={`border-t border-brand-line ${p.status === "cancelled" ? "opacity-60 line-through" : ""}`} data-testid={`payment-row-${p.id}`}>
                                    <td className="px-3 py-2 text-brand-ink">{p.payment_date}</td>
                                    <td className="px-3 py-2 font-medium">{p.flat_label || "—"}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">{p.resident_name || "—"}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft">
                                        {(p.allocations && p.allocations.length > 1)
                                            ? <span data-testid={`payment-alloc-count-${p.id}`}>{p.allocations.length} invoices</span>
                                            : (p.invoice_month || "—")}
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">{inrFull(p.amount)}</td>
                                    <td className="px-3 py-2">{MODE_LABEL[p.payment_mode]}</td>
                                    <td className="px-3 py-2 text-brand-inkSoft text-xs">{p.reference_number || p.cheque_number || "—"}</td>
                                    <td className="px-3 py-2"><Chip variant={STATUS_VARIANT[p.status] || "neutral"}>{p.status}</Chip></td>
                                    <td className="px-3 py-2 text-xs text-brand-inkSoft">{p.recorded_by_name || "—"}</td>
                                    <td className="px-3 py-2 text-right">
                                        {p.status === "confirmed" && (
                                            <button onClick={() => cancelPayment(p)} className="text-xs text-brand-inkSoft hover:text-red-600" data-testid={`btn-cancel-${p.id}`} title="Cancel payment">
                                                <Prohibit size={14} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <RecordPaymentDialog
                open={recordOpen}
                onOpenChange={setRecordOpen}
                flats={flats}
                flatById={flatById}
                onSaved={load}
            />
        </div>
    );
}

function cleanParams(o) {
    const out = {};
    for (const k of Object.keys(o)) if (o[k]) out[k] = o[k];
    return out;
}

// ================================================================
// Record Payment dialog — multi-invoice allocation supported
// ================================================================
function RecordPaymentDialog({ open, onOpenChange, flats, flatById, onSaved }) {
    const [step, setStep] = useState(1);
    const [flatId, setFlatId] = useState("");
    const [outstanding, setOutstanding] = useState([]);
    const [loadingInv, setLoadingInv] = useState(false);
    // allocations: { invoice_id: amountString }. Only invoices with an entry are "selected".
    const [alloc, setAlloc] = useState({});
    const [payDate, setPayDate] = useState(today());
    const [mode, setMode] = useState("cash");
    const [reference, setReference] = useState("");
    const [chequeNo, setChequeNo] = useState("");
    const [chequeDate, setChequeDate] = useState("");
    const [bankName, setBankName] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setStep(1); setFlatId(""); setOutstanding([]); setAlloc({});
            setPayDate(today()); setMode("cash"); setReference("");
            setChequeNo(""); setChequeDate(""); setBankName(""); setNotes(""); setSubmitting(false);
        }
    }, [open]);

    useEffect(() => {
        if (!flatId) { setOutstanding([]); setAlloc({}); return; }
        (async () => {
            setLoadingInv(true);
            try {
                const r = await api.get("/payments/invoices/outstanding", { params: { flat_id: flatId } });
                setOutstanding(r.data);
                setAlloc({});
            } catch (e) { toast.error(formatError(e)); }
            finally { setLoadingInv(false); }
        })();
    }, [flatId]);

    const flat = flatById[flatId];
    const invById = useMemo(() => Object.fromEntries(outstanding.map(i => [i.id, i])), [outstanding]);
    const selectedIds = Object.keys(alloc);
    const totalAmount = useMemo(() =>
        selectedIds.reduce((sum, id) => sum + (parseFloat(alloc[id]) || 0), 0),
        [alloc, selectedIds]
    );

    function toggleInvoice(inv) {
        setAlloc(prev => {
            const next = { ...prev };
            if (next[inv.id] !== undefined) {
                delete next[inv.id];
            } else {
                next[inv.id] = String(inv.outstanding);
            }
            return next;
        });
    }
    function setAllocAmount(id, value) {
        setAlloc(prev => ({ ...prev, [id]: value }));
    }
    function selectAll() {
        const next = {};
        outstanding.forEach(i => { next[i.id] = String(i.outstanding); });
        setAlloc(next);
    }
    function clearAll() { setAlloc({}); }

    function validateAllocations() {
        if (selectedIds.length === 0) return "Select at least one invoice";
        for (const id of selectedIds) {
            const inv = invById[id];
            const n = parseFloat(alloc[id]);
            if (isNaN(n) || n <= 0) return `Amount for ${inv?.month || "invoice"} must be greater than zero`;
            if (n > inv.outstanding + 0.005) return `Amount for ${inv.month || "invoice"} exceeds outstanding ${inrFull(inv.outstanding)}`;
        }
        if (!payDate) return "Payment date is required";
        if (mode === "cheque" && (!chequeNo || !chequeDate)) return "Cheque number and date are required for cheque payments";
        return null;
    }

    function goToConfirm() {
        const err = validateAllocations();
        if (err) return toast.error(err);
        setStep(2);
    }

    async function submit() {
        if (submitting) return;
        setSubmitting(true);
        try {
            const allocations = selectedIds.map(id => ({ invoice_id: id, amount: parseFloat(alloc[id]) }));
            const res = await api.post("/payments", {
                allocations,
                payment_date: payDate,
                payment_mode: mode,
                reference_number: (mode === "upi" || mode === "bank_transfer" || mode === "online" || mode === "other") ? (reference || null) : null,
                cheque_number: mode === "cheque" ? (chequeNo || null) : null,
                cheque_date: mode === "cheque" ? (chequeDate || null) : null,
                bank_name: mode === "cheque" ? (bankName || null) : null,
                notes: notes || null,
            });
            const issued = (res.data && res.data.receipts_issued) || [];
            if (issued.length > 0) {
                const nums = issued.map(r => r.receipt_number).join(", ");
                toast.success(`Payment recorded — receipt${issued.length > 1 ? "s" : ""} ${nums} issued`, { duration: 6000 });
            } else if (allocations.length > 1) {
                toast.success(`Payment recorded across ${allocations.length} invoices (partial — no receipt yet)`);
            } else {
                toast.success("Payment recorded (partial — receipt will be issued once the invoice is fully paid)");
            }
            onOpenChange(false);
            onSaved && onSaved();
        } catch (e) { toast.error(formatError(e)); }
        finally { setSubmitting(false); }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="record-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Record payment</DialogTitle>
                    <DialogDescription>
                        {step === 1 ? "Select invoices to pay against. One payment can clear multiple invoices at once." : "Review before confirming — this creates an authoritative payment record."}
                    </DialogDescription>
                </DialogHeader>

                {step === 1 && (
                    <div className="space-y-4">
                        <div>
                            <Label>Flat</Label>
                            <Select value={flatId} onValueChange={setFlatId}>
                                <SelectTrigger className="rounded-sm border-brand-line mt-1" data-testid="rec-flat"><SelectValue placeholder="Choose a flat" /></SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {flats.map(f => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            {flat && <p className="text-xs text-brand-inkSoft mt-1">Flat {flat.block}-{flat.number} · {flat.occupancy}</p>}
                        </div>

                        {flatId && (
                            <div>
                                <div className="flex items-baseline justify-between mb-1">
                                    <Label>Outstanding invoices</Label>
                                    {outstanding.length > 0 && (
                                        <div className="flex gap-2 text-[11px]">
                                            <button type="button" onClick={selectAll} className="text-brand-action hover:underline" data-testid="rec-select-all">Select all ({outstanding.length})</button>
                                            {selectedIds.length > 0 && <button type="button" onClick={clearAll} className="text-brand-inkSoft hover:text-red-600" data-testid="rec-clear">Clear</button>}
                                        </div>
                                    )}
                                </div>
                                {loadingInv ? <p className="text-xs text-brand-inkSoft mt-1">Loading invoices…</p> :
                                    outstanding.length === 0 ? <p className="text-xs text-brand-inkSoft mt-1 py-6 text-center border border-dashed border-brand-line rounded-sm">No outstanding invoices for this flat.</p> :
                                    <div className="mt-1 max-h-64 overflow-y-auto border border-brand-line rounded-sm divide-y divide-brand-line">
                                        {outstanding.map(inv => {
                                            const checked = alloc[inv.id] !== undefined;
                                            return (
                                                <div key={inv.id} className={`px-3 py-2 text-sm ${checked ? "bg-brand-sage" : ""}`} data-testid={`rec-invoice-row-${inv.id}`}>
                                                    <div className="flex items-center gap-3">
                                                        <input type="checkbox" checked={checked} onChange={() => toggleInvoice(inv)} data-testid={`rec-invoice-${inv.id}`} className="accent-[#FF6B1A]" />
                                                        <span className="flex-1">
                                                            <span className="text-brand-ink font-medium">{inv.month || inv.description || "Invoice"}</span>
                                                            <span className="text-xs text-brand-inkSoft ml-2">Due {inv.due_date}</span>
                                                        </span>
                                                        <span className="text-right">
                                                            <span className="block text-brand-ink font-medium">{inrFull(inv.outstanding)}</span>
                                                            <span className="block text-[10px] text-brand-inkSoft">of {inrFull(inv.amount)}</span>
                                                        </span>
                                                        <Chip variant={STATUS_VARIANT[inv.status] || "neutral"}>{inv.status}</Chip>
                                                    </div>
                                                    {checked && (
                                                        <div className="mt-2 pl-6 flex items-center gap-2">
                                                            <span className="text-[11px] text-brand-inkSoft">Allocate</span>
                                                            <Input type="number" min="0.01" step="0.01" max={inv.outstanding}
                                                                value={alloc[inv.id]} onChange={e => setAllocAmount(inv.id, e.target.value)}
                                                                data-testid={`rec-alloc-${inv.id}`}
                                                                className="h-8 rounded-sm border-brand-line text-sm max-w-[140px]" />
                                                            <span className="text-[11px] text-brand-inkSoft">of {inrFull(inv.outstanding)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                }
                                {selectedIds.length > 0 && (
                                    <div className="mt-2 flex items-center justify-between px-3 py-2 bg-brand-bg border border-brand-line rounded-sm text-sm">
                                        <span className="text-brand-inkSoft">Total amount received</span>
                                        <span className="font-medium text-brand-ink" data-testid="rec-total">{inrFull(totalAmount)}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedIds.length > 0 && (
                            <>
                                <div className="grid md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>Payment date</Label>
                                        <Input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} data-testid="rec-date" className="rounded-sm border-brand-line mt-1" />
                                    </div>
                                    <div>
                                        <Label>Payment mode</Label>
                                        <Select value={mode} onValueChange={setMode}>
                                            <SelectTrigger className="rounded-sm border-brand-line mt-1" data-testid="rec-mode"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(MODE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {mode === "cheque" && (
                                    <div className="grid md:grid-cols-3 gap-3">
                                        <div>
                                            <Label>Cheque number</Label>
                                            <Input value={chequeNo} onChange={e => setChequeNo(e.target.value)} data-testid="rec-cheque-no" className="rounded-sm border-brand-line mt-1" />
                                        </div>
                                        <div>
                                            <Label>Cheque date</Label>
                                            <Input type="date" value={chequeDate} onChange={e => setChequeDate(e.target.value)} data-testid="rec-cheque-date" className="rounded-sm border-brand-line mt-1" />
                                        </div>
                                        <div>
                                            <Label>Bank name</Label>
                                            <Input value={bankName} onChange={e => setBankName(e.target.value)} data-testid="rec-bank" className="rounded-sm border-brand-line mt-1" />
                                        </div>
                                    </div>
                                )}
                                {(mode === "upi" || mode === "bank_transfer" || mode === "online" || mode === "other") && (
                                    <div>
                                        <Label>Reference number</Label>
                                        <Input value={reference} onChange={e => setReference(e.target.value)} placeholder="Transaction / UTR / receipt no." data-testid="rec-reference" className="rounded-sm border-brand-line mt-1" />
                                    </div>
                                )}

                                <div>
                                    <Label>Notes (optional)</Label>
                                    <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="rounded-sm border-brand-line mt-1" data-testid="rec-notes" />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-3">
                        <div className="bg-brand-bg border border-brand-line rounded-sm p-4 text-sm space-y-3">
                            <Row label="Flat" value={flat ? `${flat.block}-${flat.number}` : "—"} />
                            <Row label="Payment mode" value={MODE_LABEL[mode]} />
                            {mode === "cheque" && chequeNo && <Row label="Cheque" value={`#${chequeNo} · ${chequeDate}${bankName ? ` · ${bankName}` : ""}`} />}
                            {reference && <Row label="Reference" value={reference} />}
                            <Row label="Payment date" value={payDate} />

                            <div className="border-t border-brand-line pt-3">
                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-2">Allocations ({selectedIds.length} invoice{selectedIds.length > 1 ? "s" : ""})</p>
                                <div className="space-y-2">
                                    {selectedIds.map(id => {
                                        const inv = invById[id];
                                        const paid = parseFloat(alloc[id]) || 0;
                                        const newRemaining = Math.max(inv.outstanding - paid, 0);
                                        const fully = paid + 0.005 >= inv.outstanding;
                                        return (
                                            <div key={id} className="flex items-center justify-between text-xs" data-testid={`review-alloc-${id}`}>
                                                <div className="flex-1">
                                                    <span className="text-brand-ink font-medium">{inv.month || "Invoice"}</span>
                                                    <span className="text-brand-inkSoft ml-2">outstanding {inrFull(inv.outstanding)}</span>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-brand-ink font-medium">{inrFull(paid)}</div>
                                                    <div className={`text-[10px] ${fully ? "text-[#059669]" : "text-[#EA580C]"}`}>
                                                        {fully ? "clears invoice" : `remains ${inrFull(newRemaining)}`}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="border-t border-brand-line pt-2 flex justify-between text-sm">
                                <span className="text-brand-ink font-medium">Total being recorded</span>
                                <span className="text-brand-ink font-medium">{inrFull(totalAmount)}</span>
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter className="flex-wrap gap-2">
                    {step === 2 && (
                        <Button variant="outline" className="rounded-full" onClick={() => setStep(1)} data-testid="rec-back">
                            <ArrowLeft size={14} className="mr-1" /> Back
                        </Button>
                    )}
                    <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
                    {step === 1 ? (
                        <Button className="rounded-full" onClick={goToConfirm} data-testid="rec-next" disabled={selectedIds.length === 0}>Review</Button>
                    ) : (
                        <Button className="rounded-full" onClick={submit} data-testid="rec-confirm" disabled={submitting}>
                            {submitting ? "Recording…" : `Confirm ${inrFull(totalAmount)}`}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, value, highlight }) {
    return (
        <div className="flex justify-between">
            <span className="text-brand-inkSoft">{label}</span>
            <span className={`${highlight ? "font-medium text-brand-ink" : "text-brand-ink"}`}>{value}</span>
        </div>
    );
}
